import { useRef } from "react";
import * as yup from 'yup';
import { Formik } from 'formik';
import { Button as BSButton, Col, FloatingLabel, Form, Row } from "react-bootstrap";
import ReactTextareaAutosize from "react-textarea-autosize";
import { ProviderModel, ProviderTypeEnum } from "../../../data/entities";
import { useAsync, useUrlParams } from "../../../hooks";
import {
  Button,
  FormSectionDivider,
  FormSectionHeader,
  HandleDropDownListChange,
  SearchableProviderTypeDropdown,
  SearchableUserDropdown,
  SlideOutModal,
  SlideOutModalProps,
} from "../../../components";
import { getUserInfo, getUserTenantId } from "../../../app";
import { useSelector } from "react-redux";
import { UserLocationService } from "../../../services";

export interface AddProviderModalProps extends SlideOutModalProps {
  onSubmit: any;
}

export function AddProviderModal(props: Readonly<AddProviderModalProps>) {

  const submitRef = useRef<HTMLButtonElement>(null);
  const locationId = parseInt(useUrlParams("id"));
  const tenantId = useSelector(getUserTenantId());
  const userLocationService = new UserLocationService();
  const userLocations = useSelector(getUserInfo())?.locationIds;
  
  const locations = userLocations?.join(",") ?? ""; 
  const { value: users } = useAsync(async () => await userLocationService.getUsersByLocationAsync(locations));

  const usersAvailableInLocation = users?.filter(user => user.locationId === locationId);

  let userSet = new Set<number>();
  usersAvailableInLocation?.forEach(user => userSet.add(user.userId));
  const filtereUsers = [...userSet]
  
  const validationSchema = yup.object().shape({
    name: yup.string().required("Name is required"),
    providerType: yup.string()
      .oneOf(Object.values(ProviderTypeEnum))
      .required("Provider Type is required"),
    description: yup.string().notRequired(),
    capacity: yup.number().min(0, "Capacity must be greater than or equal to 0").required("Capacity is required"),
    locationId: yup.number().required("Location is required"),
    userId: yup.number().when('providerType', (providerType: ProviderTypeEnum | any[], schema) => {
      return (Object.values(ProviderTypeEnum) as ProviderTypeEnum[]).includes(providerType as ProviderTypeEnum)
        ? schema.required("User is required")
        : schema.notRequired();
    })
  });


  return (
    <SlideOutModal {...props} title="New Provider"
      footer={
        <div className="container p-0">
          <div className="row g-0">
            <div className="col-12 pe-2">
              <Button onClick={() => props.showState.setter(false)} variant="cancel" className="w-100">
                Cancel
              </Button>
            </div>
            <div className="col-12 ps-2">
              <Button onClick={() => submitRef.current?.click()} variant="primary" className="w-100">
                Add
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <Formik
        validationSchema={validationSchema}
        initialValues={{
          id: 0,
          name: '',
          providerType: '' as ProviderTypeEnum,
          description: '',
          capacity: 0,
          userId: null,
          locationId: 0,
        }}
        onSubmit={async (values) => {
          const provider = {
            providerType: values.providerType,
            description: values.description,
            capacity: values.capacity,
            userId: values.providerType == ProviderTypeEnum.person ? values.userId : null,
            locationId: locationId,
            tenantId: tenantId,
            name: values.name,
            isActive: true
          };
          props.onSubmit(provider as ProviderModel);
        }}
      >
        {({ values, handleSubmit, handleChange, touched, setFieldValue, errors }) => {
          return (
            <Form onSubmit={handleSubmit} className="px-2 pt-2">

              {/* Identity */}
              <FormSectionHeader>Identity</FormSectionHeader>
              <Row>
                <Col>
                  <Form.Group controlId="providerType">
                      <SearchableProviderTypeDropdown
                        name="providerType"
                        value={values.providerType}
                        placeholder="Type"
                        onChange={(e) => HandleDropDownListChange(e, setFieldValue)}
                        isInvalid={touched.providerType && !!errors.providerType}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.providerType}
                      </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {values.providerType === ProviderTypeEnum.person && (
                <Row>
                  <Col>
                    <Form.Group controlId="userId">
                        <SearchableUserDropdown
                          filterUsers={filtereUsers}
                          defaultItem={true}
                          name="userId"
                          value={values.userId}
                          placeholder="User"
                          onChange={(e) => HandleDropDownListChange(e, setFieldValue)}
                          isInvalid={touched.userId && !!errors.userId}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.userId}
                        </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              )}

              <FormSectionDivider />

              {/* Profile */}
              <FormSectionHeader>Profile</FormSectionHeader>
              <Row>
                <Col>
                  <Form.Group controlId="name">
                    <FloatingLabel label="Name" className="mb-3">
                      <Form.Control
                        type="text"
                        placeholder="Name"
                        name="name"
                        value={values.name}
                        onChange={handleChange}
                        isInvalid={touched.name && !!errors.name}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Form.Group controlId="description">
                    <FloatingLabel label="Description" className="mb-3">
                      <ReactTextareaAutosize
                        minRows={4}
                        className="form-control"
                        placeholder="Description"
                        name="description"
                        value={values.description}
                        onChange={handleChange}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.description}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Form.Group>
                </Col>
              </Row>

              <FormSectionDivider />

              {/* Configuration */}
              <FormSectionHeader>Configuration</FormSectionHeader>
              <Row>
                <Col>
                  <Form.Group controlId="capacity">
                    <FloatingLabel label="Capacity" className="mb-1">
                      <Form.Control
                        type="number"
                        name="capacity"
                        placeholder="Capacity"
                        value={values.capacity}
                        onChange={handleChange}
                        isInvalid={touched.capacity && !!errors.capacity}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.capacity}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                    <Form.Text className="text-body-secondary">Concurrent bookings allowed.</Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <BSButton ref={submitRef} type="submit" size="lg" className="d-none" />
            </Form>
          )
        }}
      </Formik>
    </SlideOutModal>
  );
}

export default AddProviderModal;
